import * as Schema from "effect/Schema";

/**
 * Whether the Environment Service Launcher can be reached by typing its name,
 * and what the App can do about it when it cannot.
 *
 * Desktop-only: the launcher is installed by Desktop onto the machine Desktop
 * runs on, so a paired remote browser asking this question would be asking
 * about somebody else's filesystem.
 */
export const EnvironmentServiceLauncherStatusSchema = Schema.Struct({
  /** Where the launcher is, whether or not anything can find it. */
  launcherPath: Schema.String,
  /** Whether typing `afar-research` finds it, from any of this install's launchers. */
  reachable: Schema.Boolean,
  /** Absent where this platform has no System Launcher, as on Windows. */
  systemLauncherPath: Schema.optional(Schema.String),
  systemLauncherInstalled: Schema.Boolean,
  /**
   * Whether the App can offer to add the System Launcher. False where there is
   * no qualified escalation, so the renderer never offers an action that cannot
   * work rather than discovering that at the point of pressing it.
   */
  canInstallSystemLauncher: Schema.Boolean,
  /** Whether the App can offer to take the System Launcher away again. */
  canRemoveSystemLauncher: Schema.Boolean,
});
export type EnvironmentServiceLauncherStatus = typeof EnvironmentServiceLauncherStatusSchema.Type;

/**
 * `cancelled` is dismissing the authentication dialog, which is an ordinary
 * answer to being asked and must not read as a fault.
 */
export const SystemLauncherActionOutcomeSchema = Schema.Literals([
  "installed",
  "removed",
  "cancelled",
  "failed",
]);
export type SystemLauncherActionOutcome = typeof SystemLauncherActionOutcomeSchema.Type;

export const SystemLauncherActionResultSchema = Schema.Struct({
  outcome: SystemLauncherActionOutcomeSchema,
  /** The state after the attempt, so one round trip both acts and refreshes. */
  status: EnvironmentServiceLauncherStatusSchema,
  /**
   * Why a `failed` attempt failed, as the host reported it. Carried rather than
   * logged: a surface that exists because a report was written somewhere nobody
   * reads must not do the same thing one layer up.
   */
  detail: Schema.optional(Schema.String),
});
export type SystemLauncherActionResult = typeof SystemLauncherActionResultSchema.Type;

export interface DesktopEnvironmentServiceLauncherBridge {
  getStatus: () => Promise<EnvironmentServiceLauncherStatus>;
  installSystemLauncher: () => Promise<SystemLauncherActionResult>;
  removeSystemLauncher: () => Promise<SystemLauncherActionResult>;
}
